import { token } from '@bp-files/ui';
import { InkPreview } from '@/components/Signature/InkPreview';
import { FRAME_BUTTON_CLASS, FRAME_TOUCH_BUTTON_CLASS } from '@/components/Viewer/ViewerFrame';
import { AddSignatureIcon, PencilIcon, TextFieldIcon, UndoIcon } from './PdfIcons';
import { SIGNING_COPY } from './Signing/signing.copy';
import type { PdfControls } from './pdfControls';

/**
 * The two arming controls — place a signature, place a text field (FIL-8c).
 *
 * Shared by the desktop toolbar and the mobile bottom bar, which is the reason
 * they are a component rather than JSX in each: the sign button carries five
 * interacting conditions (write access, a stored mark or the lack of one, armed
 * or not, saving) and two copies of that is two places for them to drift.
 *
 * Only ever rendered by one bar at a time. The viewport picks which.
 */
export function PdfSignTools({
  controls,
  touch = false,
}: {
  controls: PdfControls;
  /** Thumb-sized rather than cursor-sized, and Sign made the prominent action. */
  touch?: boolean;
}) {
  const { canSign, signing, onNeedsSignature } = controls;
  const buttonClass = touch ? FRAME_TOUCH_BUTTON_CLASS : FRAME_BUTTON_CLASS;

  return (
    <>
      {/*
        **Draw first — the one tool that needs no stored signature.** The pencil
        draws its own ink onto the page, so it is offered to any writer, and it
        leads the group as the entry that is always here. It is a plain toggle,
        not a "place": it stays armed across strokes and commits on the next
        tool switch or Save (`useSigning`), so its label names the act.
      */}
      {canSign ? (
        <button
          type="button"
          disabled={signing.saving}
          aria-pressed={signing.tool === 'draw'}
          aria-label={
            signing.tool === 'draw' ? SIGNING_COPY.tools.stopDraw : SIGNING_COPY.tools.draw
          }
          onClick={() => {
            signing.chooseTool(signing.tool === 'draw' ? undefined : 'draw');
          }}
          className={`${buttonClass} text-secondary hover:text-body`}
          style={
            signing.tool === 'draw'
              ? { background: token('bp-blue'), color: token('bp-on-accent') }
              : undefined
          }
        >
          <PencilIcon />
        </button>
      ) : null}

      {/*
        **Text first, Sign second, on both breakpoints.** The two are not peers:
        a text field is an annotation and a signature is the act the whole mode
        exists for, so Sign is the last thing before the reader leaves the group
        — and on a phone it is the rightmost, largest target in the bar. Its
        white chip already makes it the loudest control here; putting it after
        the quieter one is what makes that read as sequence rather than as noise.

        **Its own toggle, not folded into the sign button.** A tool disarms
        itself the moment it places something (`useSigning.place`), so there is
        never a moment where both need to show pressed at once — and it stays
        gated on `signable` alone: a text field needs no stored mark, but pairing
        it with a sign button that might navigate away instead of arming would be
        a second, different kind of click sitting right next to it.
      */}
      {canSign && signing.signable ? (
        <button
          type="button"
          disabled={signing.saving}
          aria-pressed={signing.tool === 'text'}
          aria-label={
            signing.tool === 'text'
              ? SIGNING_COPY.tools.cancel(SIGNING_COPY.field.text)
              : SIGNING_COPY.tools.place(SIGNING_COPY.field.text)
          }
          onClick={() => {
            signing.chooseTool(signing.tool === 'text' ? undefined : 'text');
          }}
          className={`${buttonClass} text-secondary hover:text-body`}
          style={
            signing.tool === 'text'
              ? // `bp-on-accent`, not `bp-text` — the readme's line for solid
                // blue buttons, and the pairing every `primaryButtonStyle()` in
                // this repo already uses (ADR-0064).
                { background: token('bp-blue'), color: token('bp-on-accent') }
              : undefined
          }
        >
          <TextFieldIcon />
        </button>
      ) : null}

      {/*
        **The sign button is the entry point whether or not a mark is stored
        yet.** `signable` alone (write access and a stored mark) used to gate
        this entirely, and somebody with no signature got nothing here — only a
        small footer space to go draw one, with no way back to the document once
        they left. Now the button itself carries the signature once there is
        one, and *is* the "go set one up" control when there is not.

        **White, not the toolbar's usual dark chip.** The button holds the
        person's actual signature, which is drawn in `bp-ink` for a page, not
        for this dark room — so it needs to sit on the same white a signature is
        legible against, same as the stamp itself (`FieldLayer`) and the pad
        (`SignaturePad`). That is also why it is the prominent control on the
        phone with no extra styling: it is already the only white thing there.
      */}
      {canSign && (signing.signable || signing.needsSignature) ? (
        <button
          type="button"
          disabled={signing.saving}
          aria-pressed={signing.ink !== undefined ? signing.tool === 'signature' : undefined}
          aria-label={
            signing.ink === undefined
              ? SIGNING_COPY.needsSignature.action
              : signing.tool === 'signature'
                ? SIGNING_COPY.tools.cancel(SIGNING_COPY.field.signature)
                : SIGNING_COPY.tools.place(SIGNING_COPY.field.signature)
          }
          onClick={() => {
            if (signing.ink === undefined) {
              onNeedsSignature();
              return;
            }
            signing.chooseTool(signing.tool === 'signature' ? undefined : 'signature');
          }}
          className={`flex items-center justify-center gap-1 rounded-md px-3 transition-colors disabled:pointer-events-none disabled:opacity-40 ${
            touch ? 'min-h-11' : 'min-h-10'
          }`}
          style={{
            background: token('bp-on-accent'),
            color: token('bp-ink'),
            borderWidth: 2,
            borderStyle: 'solid',
            borderColor: signing.tool === 'signature' ? token('bp-blue') : 'transparent',
          }}
        >
          {signing.ink !== undefined ? (
            <InkPreview ink={signing.ink} width={56} height={28} />
          ) : (
            <AddSignatureIcon />
          )}
        </button>
      ) : null}
    </>
  );
}

/**
 * Undo the last local edit (FIL-8c). Shared by both bars, and shown only when
 * there is something to undo — a dead, permanently-disabled control is the
 * question the Save cluster's own comment declines to ask.
 *
 * Not folded into `PdfSaveCluster`: that appears only once there is content to
 * save, and a run of deletes leaves nothing to save while there is still
 * everything to undo. So this stands on its own, gated on `canUndo` alone.
 */
export function PdfUndoButton({
  controls,
  touch = false,
}: {
  controls: PdfControls;
  touch?: boolean;
}) {
  const { signing } = controls;
  const buttonClass = touch ? FRAME_TOUCH_BUTTON_CLASS : FRAME_BUTTON_CLASS;

  return (
    <button
      type="button"
      disabled={signing.saving}
      aria-label={SIGNING_COPY.undo.action}
      onClick={signing.undo}
      className={`${buttonClass} text-secondary hover:text-body disabled:pointer-events-none disabled:opacity-40`}
    >
      <UndoIcon />
    </button>
  );
}

/** The desktop toolbar's save control. */
export function PdfSaveCluster({ controls }: { controls: PdfControls }) {
  const { signing } = controls;

  return (
    <button
      type="button"
      disabled={signing.saving}
      aria-busy={signing.saving}
      onClick={signing.save}
      className="shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
      style={{ background: token('bp-blue'), color: token('bp-on-accent') }}
    >
      {signing.saving ? SIGNING_COPY.save.working : SIGNING_COPY.save.action}
    </button>
  );
}
